import React from "react";
import { useForm } from "react-hook-form";
import { Button, Box, Typography, Grid, Card, Tabs, Tab, Stack } from "@mui/material";
import RFG from "../../libs/inputs/RFG";
import ImageEditor from "../../libs/imgcrop/ImageEditor";
import ImageResizer from "../extra/ImageResizer";
import CountryList from 'country-list-with-dial-code-and-flag'
import ReactCountryFlag from "react-country-flag"
import CountrySelect from "../extra/CountrySelect";
import MultiSelect from "../extra/MultiSelect";
import { COUNTRY, STATE } from "../../services/actions";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { BsBuilding, BsBriefcaseFill, BsGeoAltFill, BsFileEarmarkTextFill, BsShieldCheck, BsBank, BsInfoCircleFill } from "react-icons/bs";
import { generateFakeClientData } from "../../libs/testDataGenerator";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <>{children}</>}
        </div>
    );
}

const SectionHeader = ({ icon, title, subtitle }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={2.5} pb={1.5} borderBottom="1px solid #f1f5f9">
        <Box sx={{
            width: 38, height: 38, borderRadius: '10px',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(37, 99, 235, 0.08)',
            flexShrink: 0
        }}>
            {icon}
        </Box>
        <Box>
            <Typography fontSize={15} fontWeight={700} color="#0f172a" lineHeight={1.2}>
                {title}
            </Typography>
            {subtitle && (
                <Typography fontSize={12} color="#64748b" mt={0.3}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    </Box>
);

const labelStyle = {
    fontSize: '11px',
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
    display: 'block'
};

export default function Form({
    title,
    packages = [],
    defaultValues = {},
    onSubmit = () => { },
    onCancel = () => { },
}) {
    const dispatch = useDispatch();
    const [tabValue, setTabValue] = React.useState(0);
    const { handleSubmit, control, setValue, watch, clearErrors, register, formState: { errors } } = useForm({
        defaultValues,
        mode: "all",
    });

    const [countries, setCountries] = React.useState([]);
    const [states, setStates] = React.useState([]);
    // Holds all model choices fetched from backend: { status: [{value,label}], business_entity: [...], ... }
    const [choices, setChoices] = React.useState({});

    // Fetch model choices from backend on mount
    React.useEffect(() => {
        const token = JSON.parse(localStorage.getItem("auth") || '{}')?.token;
        axios.get(`${process.env.REACT_APP_URL}client/choices/`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).then(res => {
            const data = res?.data?.data || res?.data;
            console.log("client choices", data);
            setChoices(data || {});
        }).catch(err => {
            console.warn("Could not fetch client choices:", err);
        });
    }, []);

    React.useEffect(() => {
        dispatch({
            type: `${COUNTRY}_GET`, payload: 'countries', callback: (data) => {
                console.log("countries", data);
                setCountries(data?.data);
            }
        });
    }, []);

    const getStates = (country) => {
        dispatch({
            type: `${STATE}_GET`, payload: `states?country=${country}`, callback: (data) => {
                console.log("states", data);
                setStates(data?.data?.states || []);
            }
        });
    };

    React.useEffect(() => {
        if (watch("country")) getStates(watch("country"));
    }, [watch("country")]);

    function applyProps(index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    const handleFillFakeData = () => {
        const fakeData = generateFakeClientData(choices, countries);
        Object.entries(fakeData).forEach(([key, value]) => {
            setValue(key, value, { shouldValidate: true });
        });
        if (fakeData.country) {
            getStates(fakeData.country);
            setValue("state", fakeData.state, { shouldValidate: true });
        }
        clearErrors();
    };

    const showAutoFill = process.env.REACT_APP_DEBUG !== "false" && process.env.REACT_APP_DEBUG !== "False" && process.env.REACT_APP_DEBUG !== "0";

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ py: 3, px: { xs: 1, md: 3 }, maxWidth: '1400px', mx: 'auto' }}>
                {/* Premium Hero Header */}
                <Box sx={{
                    mb: 4, p: { xs: 2.5, md: 3.5 },
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box sx={{
                            width: 52, height: 52, borderRadius: '14px',
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                            flexShrink: 0
                        }}>
                            <BsBuilding size={26} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', mb: 0.5, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                                {title || "Client Profile Setup"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                                Configure business entity, tax identifiers, and location details for this client record.
                            </Typography>
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        {showAutoFill && (
                            <Button
                                data-testid="btn-auto-fill-fake-top"
                                type="button"
                                variant="outlined"
                                onClick={handleFillFakeData}
                                sx={{
                                    borderColor: '#10b981',
                                    color: '#059669',
                                    bgcolor: '#ecfdf5',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    borderRadius: '20px',
                                    px: 2.5, py: 0.8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.15)',
                                    '&:hover': { bgcolor: '#d1fae5', borderColor: '#059669', transform: 'translateY(-1px)' }
                                }}
                            >
                                ⚡ Auto-Fill Sample Data
                            </Button>
                        )}
                        <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: '#eff6ff', px: 2, py: 0.8, borderRadius: '20px', border: '1px solid #bfdbfe' }}>
                            <BsShieldCheck color="#2563eb" size={16} />
                            <Typography fontSize={12} fontWeight={700} color="#1e40af">Verified Entity Record</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Core Business Details Card */}
                <Card sx={{ p: { xs: 2.5, md: 3.5 }, mb: 4, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.03)', bgcolor: '#ffffff', overflow: 'visible' }}>
                    <SectionHeader icon={<BsBriefcaseFill size={18} />} title="Core Business Information" subtitle="Primary identity and registration classification" />
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={2.5}>
                                <RFG name="business_entity" label="Business Entity *" control={control} rules={{ required: true }} labelStyle={labelStyle}>
                                    <select>
                                        <option value="">Select Business Entity</option>
                                        {(choices?.business_entity?.length
                                            ? choices.business_entity
                                            : [
                                                { value: "pvt_ltd", label: "Private Limited Company (Pvt Ltd)" },
                                                { value: "llp", label: "Limited Liability Partnership (LLP)" },
                                                { value: 'opc', label: 'One Person Company (OPC)' },
                                                { value: 'plc', label: 'Public Limited Company' },
                                                { value: 'sp', label: 'Sole Proprietorship' },
                                                { value: 'pf', label: 'Partnership Firm' },
                                            ]
                                        ).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </RFG>
                                <RFG name="currency" label="Currency *" control={control} rules={{ required: true }} labelStyle={labelStyle}>
                                    <select>
                                        <option value="">Select Currency</option>
                                        {[{ id: "Rupees", label: "Rupees" }, { id: "Dollar", label: "Dollar" }].map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                    </select>
                                </RFG>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={2.5}>
                                <RFG
                                    name="business_name"
                                    label="Business Name *"
                                    control={control}
                                    labelStyle={labelStyle}
                                    rules={{
                                        required: true, maxLength: {
                                            value: 100,
                                            message: `Cannot exceed 100 characters`,
                                        },
                                        minLength: {
                                            value: 3,
                                            message: "Cannot be less than 3 characters",
                                        }
                                    }}
                                >
                                    <input placeholder="e.g. Acme Corporation Inc." autoComplete="off" />
                                </RFG>

                                <RFG
                                    name="client_creation_date"
                                    label="Client Creation Date *"
                                    control={control}
                                    labelStyle={labelStyle}
                                    rules={{
                                        required: true
                                    }}
                                >
                                    <input type="date" autoComplete="off" />
                                </RFG>
                            </Stack>
                        </Grid>
                    </Grid>
                </Card>

                {/* Segmented Pill Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => setTabValue(v)}
                        aria-label="client form tabs"
                        sx={{
                            minHeight: '44px',
                            '& .MuiTabs-indicator': {
                                height: '3px',
                                borderRadius: '3px 3px 0 0',
                                background: 'linear-gradient(90deg, #2563eb, #3b82f6)'
                            }
                        }}
                    >
                        <Tab
                            data-testid="gst-address-tab"
                            icon={<BsGeoAltFill size={16} style={{ marginBottom: 0, marginRight: '8px' }} />}
                            iconPosition="start"
                            label="GST & Address"
                            {...applyProps(0)}
                            sx={{
                                fontWeight: 700,
                                fontSize: '13px',
                                textTransform: 'none',
                                minHeight: '44px',
                                color: tabValue === 0 ? '#2563eb' : '#64748b',
                                transition: 'all 0.2s',
                                '&:hover': { color: '#1e293b', bgcolor: '#f8fafc' }
                            }}
                        />
                        <Tab
                            data-testid="other-details-tab"
                            icon={<BsFileEarmarkTextFill size={16} style={{ marginBottom: 0, marginRight: '8px' }} />}
                            iconPosition="start"
                            label="Other Details"
                            {...applyProps(1)}
                            sx={{
                                fontWeight: 700,
                                fontSize: '13px',
                                textTransform: 'none',
                                minHeight: '44px',
                                color: tabValue === 1 ? '#2563eb' : '#64748b',
                                transition: 'all 0.2s',
                                '&:hover': { color: '#1e293b', bgcolor: '#f8fafc' }
                            }}
                        />
                    </Tabs>
                </Box>

                {/* Tab Panel 0: GST & Address */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Card sx={{ p: { xs: 2.5, md: 3 }, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.03)', bgcolor: '#ffffff', overflow: 'visible', height: '100%' }}>
                                <SectionHeader icon={<BsShieldCheck size={18} />} title="Tax & Classification" subtitle="GST identification and current account status" />
                                <Stack spacing={2.5}>
                                    <RFG
                                        name="gstin"
                                        label="GSTIN"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            pattern: {
                                                value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i,
                                                message: "Enter a valid 15-character GSTIN (e.g. 27AADCB2230M1Z2)"
                                            }
                                        }}
                                    >
                                        <input placeholder="e.g. 27AADCB2230M1Z2" autoComplete="off" maxLength={15} style={{ textTransform: 'uppercase' }} />
                                    </RFG>

                                    <RFG name="type" label="Type" control={control} labelStyle={labelStyle}>
                                        {choices?.type?.length ? (
                                            <select>
                                                <option value="">Select Type</option>
                                                {choices.type.map((c) => (
                                                    <option key={c.value} value={c.value}>{c.label}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input placeholder="e.g. Regular / Composition" autoComplete="off" />
                                        )}
                                    </RFG>

                                    <RFG name="status" label="Status *" control={control} rules={{ required: true }} labelStyle={labelStyle}>
                                        <select>
                                            <option value="">Select Status</option>
                                            {(choices?.status?.length
                                                ? choices.status
                                                : [
                                                    { value: "active", label: "Active" },
                                                    { value: "inactive", label: "Inactive" },
                                                ]
                                            ).map((c) => (
                                                <option key={c.value} value={c.value}>{c.label}</option>
                                            ))}
                                        </select>
                                    </RFG>

                                    <RFG name="gst_registration_date" label="GST Registration Date" control={control} labelStyle={labelStyle}>
                                        <input type="date" autoComplete="off" />
                                    </RFG>
                                </Stack>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, md: 7 }}>
                            <Card sx={{ p: { xs: 2.5, md: 3 }, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.03)', bgcolor: '#ffffff', overflow: 'visible', height: '100%' }}>
                                <SectionHeader icon={<BsGeoAltFill size={18} />} title="Location & Address" subtitle="Primary business address and jurisdiction" />
                                <Grid container spacing={2.5}>
                                    <Grid size={{ xs: 12 }}>
                                        <RFG
                                            name="place_of_supply"
                                            label="Place of Supply *"
                                            control={control}
                                            labelStyle={labelStyle}
                                            rules={{
                                                required: true,
                                                maxLength: { value: 100, message: "Cannot exceed 100 characters" }
                                            }}
                                        >
                                            <input placeholder="e.g. Maharashtra (27)" autoComplete="off" maxLength={100} />
                                        </RFG>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <RFG
                                            name="address_line1"
                                            label="Address Line 1"
                                            control={control}
                                            labelStyle={labelStyle}
                                            rules={{
                                                maxLength: { value: 200, message: "Cannot exceed 200 characters" }
                                            }}
                                        >
                                            <input placeholder="Building / Street address" autoComplete="off" maxLength={200} />
                                        </RFG>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <RFG
                                            name="address_line2"
                                            label="Address Line 2"
                                            control={control}
                                            labelStyle={labelStyle}
                                            rules={{
                                                maxLength: { value: 200, message: "Cannot exceed 200 characters" }
                                            }}
                                        >
                                            <input placeholder="Suite / Floor / Landmark" autoComplete="off" maxLength={200} />
                                        </RFG>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <input type="hidden" {...register("country", { required: "Country is required" })} />
                                        <MultiSelect
                                            multiple={false}
                                            label="Country *"
                                            name="country"
                                            control={control}
                                            value={watch("country")}
                                            options={countries.map((c) => ({ label: c?.name, id: c?.name }))}
                                            onValueChange={(value) => {
                                                const str = Array.isArray(value) ? value[0] : value;
                                                setValue("country", str || "", { shouldValidate: true });
                                                if (str) clearErrors("country");
                                            }}
                                        />
                                        {errors.country && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                                                {errors.country?.message || "Country is required"}
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <input type="hidden" {...register("state", { required: "State is required" })} />
                                        <MultiSelect
                                            multiple={false}
                                            label="State *"
                                            name="state"
                                            control={control}
                                            value={watch("state")}
                                            options={states.map((c) => ({ label: c?.name, id: c?.name }))}
                                            onValueChange={(value) => {
                                                const str = Array.isArray(value) ? value[0] : value;
                                                setValue("state", str || "", { shouldValidate: true });
                                                if (str) clearErrors("state");
                                            }}
                                        />
                                        {errors.state && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                                                {errors.state?.message || "State is required"}
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <RFG
                                            name="pincode"
                                            label="Pincode"
                                            control={control}
                                            labelStyle={labelStyle}
                                            rules={{
                                                pattern: {
                                                    value: /^[A-Za-z0-9\s-]{3,10}$/,
                                                    message: "Enter a valid Pincode (3-10 alphanumeric characters)"
                                                }
                                            }}
                                        >
                                            <input placeholder="e.g. 400001" autoComplete="off" maxLength={10} style={{ textTransform: 'uppercase' }} />
                                        </RFG>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab Panel 1: Other Details */}
                <TabPanel value={tabValue} index={1}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card sx={{ p: { xs: 2.5, md: 3 }, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.03)', bgcolor: '#ffffff', overflow: 'visible', height: '100%' }}>
                                <SectionHeader icon={<BsBuilding size={18} />} title="Corporate Registrations" subtitle="Legal entity numbers and labor compliance" />
                                <Stack spacing={2.5}>
                                    <RFG
                                        name="cin_llp"
                                        label="CIN / LLPIN"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            pattern: {
                                                value: /^([U|L][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}|[A-Z]{3}-?[0-9]{4})$/i,
                                                message: "Enter valid 21-char CIN (e.g. L12345MH2023PLC012345) or 7-char LLPIN (e.g. AAA-1234)"
                                            }
                                        }}
                                    >
                                        <input placeholder="e.g. L12345MH2023PLC012345" autoComplete="off" maxLength={21} style={{ textTransform: 'uppercase' }} />
                                    </RFG>

                                    <RFG
                                        name="tan"
                                        label="TAN"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            pattern: {
                                                value: /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/i,
                                                message: "Enter a valid 10-character TAN (e.g. MUMW12345E)"
                                            }
                                        }}
                                    >
                                        <input placeholder="e.g. MUMW12345E" autoComplete="off" maxLength={10} style={{ textTransform: 'uppercase' }} />
                                    </RFG>

                                    <RFG
                                        name="professional_tax"
                                        label="Professional Tax (PT)"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            maxLength: { value: 30, message: "Cannot exceed 30 characters" }
                                        }}
                                    >
                                        <input placeholder="e.g. PT Registration Number" autoComplete="off" maxLength={30} style={{ textTransform: 'uppercase' }} />
                                    </RFG>

                                    <RFG
                                        name="esi_registration"
                                        label="ESI Registration"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            pattern: {
                                                value: /^[0-9A-Za-z/-]{10,20}$/,
                                                message: "Enter a valid ESI Registration code (10-20 characters)"
                                            }
                                        }}
                                    >
                                        <input placeholder="e.g. ESI Code Number" autoComplete="off" maxLength={20} style={{ textTransform: 'uppercase' }} />
                                    </RFG>

                                    <RFG name="date_of_registration" label="Date of Registration" control={control} labelStyle={labelStyle}>
                                        <input type="date" autoComplete="off" />
                                    </RFG>
                                </Stack>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card sx={{ p: { xs: 2.5, md: 3 }, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.03)', bgcolor: '#ffffff', overflow: 'visible', height: '100%' }}>
                                <SectionHeader icon={<BsBank size={18} />} title="Tax & Compliance Identifiers" subtitle="Permanent account number and export LUT" />
                                <Stack spacing={2.5}>
                                    <RFG
                                        name="pan"
                                        label="PAN *"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            required: true,
                                            pattern: {
                                                value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i,
                                                message: "Enter a valid PAN number (e.g. ABCDE1234F)"
                                            }
                                        }}
                                    >
                                        <input placeholder="e.g. ABCDE1234F" autoComplete="off" maxLength={10} style={{ textTransform: 'uppercase' }} />
                                    </RFG>

                                    <RFG
                                        name="udyam"
                                        label="Udyam Registration"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            pattern: {
                                                value: /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/i,
                                                message: "Enter valid Udyam format: UDYAM-XX-00-0000000"
                                            }
                                        }}
                                    >
                                        <input placeholder="e.g. UDYAM-MH-00-0000000" autoComplete="off" maxLength={19} style={{ textTransform: 'uppercase' }} />
                                    </RFG>

                                    <RFG
                                        name="provident_fund"
                                        label="Provident Fund (PF)"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            maxLength: { value: 25, message: "Cannot exceed 25 characters" }
                                        }}
                                    >
                                        <input placeholder="e.g. PF Registration Code" autoComplete="off" maxLength={25} style={{ textTransform: 'uppercase' }} />
                                    </RFG>

                                    <RFG
                                        name="lut_number"
                                        label="LUT Number"
                                        control={control}
                                        labelStyle={labelStyle}
                                        rules={{
                                            maxLength: { value: 25, message: "Cannot exceed 25 characters" }
                                        }}
                                    >
                                        <input placeholder="e.g. Letter of Undertaking Number" autoComplete="off" maxLength={25} style={{ textTransform: 'uppercase' }} />
                                    </RFG>
                                </Stack>
                            </Card>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Floating Action Footer */}
                <Box sx={{
                    mt: 4, p: { xs: 2, md: 2.5 },
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '14px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <BsInfoCircleFill color="#64748b" size={18} />
                        <Typography fontSize={13} color="#475569" fontWeight={500}>
                            Ensure all required fields marked with an asterisk (*) are accurate before saving.
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5} sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                        {showAutoFill && (
                            <Button
                                data-testid="btn-auto-fill-fake-bottom"
                                type="button"
                                variant="outlined"
                                onClick={handleFillFakeData}
                                sx={{
                                    borderColor: '#10b981',
                                    color: '#059669',
                                    bgcolor: '#ecfdf5',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    px: 2.5, py: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.15)',
                                    '&:hover': { bgcolor: '#d1fae5', borderColor: '#059669', transform: 'translateY(-1px)' }
                                }}
                            >
                                ⚡ Auto-Fill Sample Data
                            </Button>
                        )}
                        <Button
                            data-testid="back-client-btn"
                            variant="outlined"
                            onClick={onCancel}
                            sx={{
                                borderColor: '#cbd5e1',
                                color: '#475569',
                                bgcolor: '#ffffff',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '13px',
                                borderRadius: '8px',
                                px: 3, py: 1,
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8', transform: 'translateY(-1px)' }
                            }}
                        >
                            Back
                        </Button>
                        <Button
                            data-testid="save-client-btn"
                            type="submit"
                            variant="contained"
                            sx={{
                                background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                color: '#ffffff',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '13px',
                                borderRadius: '8px',
                                px: 4, py: 1,
                                boxShadow: '0 2px 4px rgba(37, 99, 235, 0.25)',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {defaultValues?.id ? "Update Client" : "Save Client"}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </form>
    );
}
